<template>
  <Dialog
    :visible="visible"
    header="Video Details"
    modal
    class="video-info-dialog"
    :style="{ width: '32rem' }"
    @update:visible="$emit('update:visible', $event)"
  >
    <template v-if="video">
      <div class="info-section">
        <div class="info-row">
          <span class="info-label">Show</span>
          <span class="info-value">{{ video.show_title }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Episode</span>
          <span class="info-value">{{ video.episode_title || "—" }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Podcaster</span>
          <span class="info-value">{{ video.podcaster_name }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Duration</span>
          <span class="info-value">{{ video.metadata.duration }}s</span>
        </div>
        <div class="info-row">
          <span class="info-label">Resolution</span>
          <span class="info-value">{{ video.width }}×{{ video.height }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Generated</span>
          <span class="info-value">{{
            new Date(video.created_at).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          }}</span>
        </div>
      </div>

      <div v-if="!isGroupAdminViewer" class="info-section">
        <p class="section-title">Sources</p>
        <div v-if="video.source_image_url" class="link-row">
          <span class="info-label">Source image</span>
          <a
            :href="video.source_image_url"
            target="_blank"
            rel="noopener"
            class="icon-link-btn"
            title="Open"
          >
            <i class="pi pi-external-link" />
          </a>
          <button
            class="icon-link-btn"
            title="Copy link"
            @click="$emit('copy-link', video.source_image_url)"
          >
            <i class="pi pi-copy" />
          </button>
        </div>
        <div v-if="video.metadata.source_audio_url" class="link-row">
          <span class="info-label">Source audio (snippet)</span>
          <a
            :href="video.metadata.source_audio_url"
            target="_blank"
            rel="noopener"
            class="icon-link-btn"
            title="Open"
          >
            <i class="pi pi-external-link" />
          </a>
          <button
            class="icon-link-btn"
            title="Copy link"
            @click="$emit('copy-link', video.metadata.source_audio_url)"
          >
            <i class="pi pi-copy" />
          </button>
          <router-link
            :to="`/admin/snippets?tab=library&snippetId=${video.snippet_id}`"
            class="view-in-snippets-btn"
          >
            View in Snippets
            <i class="pi pi-arrow-right" />
          </router-link>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import Dialog from "primevue/dialog";
import { snippetViewerRole } from "@/composables/useSnippetApiToken";

const isGroupAdminViewer = snippetViewerRole() === "group-admin";

defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  video: {
    type: Object,
    default: null,
  },
});

defineEmits(["update:visible", "copy-link"]);
</script>

<style scoped>
.info-section {
  margin-bottom: 1.25rem;
}

.info-section:last-of-type {
  margin-bottom: 0;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-color-secondary);
  margin: 0 0 0.5rem 0;
}

.info-row,
.link-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0;
  font-size: 0.875rem;
}

.info-label {
  flex: 0 0 9rem;
  color: var(--text-color-secondary);
}

.info-value {
  flex: 1;
  color: var(--text-color);
  word-break: break-word;
}

.icon-link-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color-secondary);
  padding: 0.25rem;
  display: inline-flex;
}

.icon-link-btn:hover {
  color: var(--primary-color);
}

.view-in-snippets-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}

.view-in-snippets-btn:hover {
  background: var(--primary-color);
  color: var(--primary-color-text);
}
</style>
